import React from 'react';

import isArchived from 'archive/isArchived';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {UserSummary} from 'constants/UserConstants';
import {ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import SidepanelItem from 'nav/sidepanel/listing/SidepanelItem';
import ProcessTaskCellTitle from 'processes/dashboard/ProcessTaskCellTitle';
import {ProcessManagementItemType} from 'processes/ProcessConstants';
import {ListableItem} from 'search/SearchConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import fuzzydate from 'util/fuzzydate';

import * as ProcessManagementContextMenu from './ProcessManagementContextMenu';
import {
  raiseInvalidProcessItemError,
  SidepanelProcessItem,
  sidepanelProcessItemIsProcess,
  sidepanelProcessItemIsProcessOutput,
  sidepanelProcessItemIsProcessTask,
} from './ProcessManagementSidepanelConstants';

interface Props {
  isActive: boolean;
  isCompact: boolean;
  processItem: SidepanelProcessItem;
  openResult: (item: ListableItem) => void;
  isChild: boolean;
}

function getSidepanelItemParams(processItem: SidepanelProcessItem): {
  title: string;
  parties: Array<UserSummary>;
  selectedType: ProcessManagementItemType;
} | null {
  let title: string = processItem.humanId;
  let parties: Array<UserSummary> = [];
  let selectedType: ProcessManagementItemType;
  const creator: UserSummary = {...processItem.creator, avatar_url: processItem.creator.avatar_url!};
  if (sidepanelProcessItemIsProcess(processItem)) {
    parties = [creator];
    title = processItem.name;
    selectedType = ModelTypeConstants.PROCESS;
  } else if (sidepanelProcessItemIsProcessOutput(processItem)) {
    parties = [creator];
    selectedType = ModelTypeConstants.PROCESS_OUTPUT;
  } else if (sidepanelProcessItemIsProcessTask(processItem)) {
    parties =
      processItem.assignee != null
        ? [{...processItem.assignee, avatar_url: processItem.assignee.avatar_url!}]
        : [];
    selectedType = ModelTypeConstants.PROCESS_TASK;

    title =
      processItem.schema.clientOrigin === ProcessClientOrigin.PROCESSES
        ? processItem.humanId
        : processItem.name;
  } else {
    raiseInvalidProcessItemError();
    return null;
  }
  return {title, parties, selectedType};
}

export default function ProcessManagementSidepanelItem(props: Props): JSX.Element {
  const {isActive, isCompact, processItem, isChild} = props;
  const date = fuzzydate(processItem.createdAt, {compact: isCompact, showTime: false});
  const subtitle = `${isCompact ? '' : 'Created '}${date}`;
  const {title, parties, selectedType} = getSidepanelItemParams(processItem)!;
  return (
    <SidepanelItem
      active={isActive}
      className="sidepanelItem--noIcon"
      faded={isArchived(processItem)}
      icon={<BenchlingIcon icon={selectedType} />}
      isCompact={isCompact}
      onClick={() => {
        props.openResult(processItem);
      }}
      parties={parties}
      subTitle={subtitle}
      title={{
        text: title,
        node: (
          <>
            {sidepanelProcessItemIsProcessTask(processItem) ? (
              <ProcessTaskCellTitle processTask={processItem} title={title} />
            ) : (
              <div>{title}</div>
            )}
          </>
        ),
      }}
      tooltipText=""
      onContextMenu={(e: React.SyntheticEvent) => {
        ProcessManagementContextMenu.open(e.nativeEvent, selectedType, processItem);
      }}
      isExpandable={false}
      isChild={isChild}
    />
  );
}
